import { useMemo, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/modern/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/modern/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import {
  ENTITY_FINDER_FOOTER_CHIP_CLS,
  ENTITY_FINDER_FOOTER_CLS,
} from "@/modern/components/entity-finder/constants/layout";
import { SURFACE_CONTROL_CLS, SURFACE_ICON_BTN_CLS } from "@/react/components/entityViews/files/constants/surfaceChrome";
import { cn } from "@/modern/utils";
import {
  Calendar,
  CalendarDays,
  CalendarRange,
  Camera,
  Check,
  FileText,
  FolderOpen,
  Image,
  Layers,
  ListFilter,
  SlidersHorizontal,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  DEFAULT_FILE_BROWSER_FILTERS,
  type DatePreset,
  type FileBrowserFilterState,
  type FileSourceTab,
  type FileTypeTab,
} from "./types";

interface FiltersPopoverProps {
  filters: FileBrowserFilterState;
  onChange: (partial: Partial<FileBrowserFilterState>) => void;
  onResetFilters: () => void;
}

type FilterDimension = "fileTypeTab" | "source" | "datePreset";

const TYPE_OPTIONS: { value: FileTypeTab; label: string; icon: LucideIcon }[] = [
  { value: "media", label: "Media", icon: Image },
  { value: "documents", label: "Documents", icon: FileText },
  { value: "all", label: "All types", icon: FolderOpen },
];

const SOURCE_OPTIONS: { value: FileSourceTab; label: string; icon: LucideIcon }[] = [
  { value: "all", label: "All sources", icon: FolderOpen },
  { value: "swivu", label: "Swivu", icon: Layers },
  { value: "companycam", label: "CompanyCam", icon: Camera },
];

const DATE_OPTIONS: { value: DatePreset; label: string; icon: LucideIcon }[] = [
  { value: "any", label: "Any date", icon: CalendarRange },
  { value: "today", label: "Today", icon: Calendar },
  { value: "yesterday", label: "Yesterday", icon: CalendarDays },
  { value: "last7", label: "Last 7 days", icon: CalendarDays },
];

function countActiveFilters(filters: FileBrowserFilterState): number {
  let n = 0;
  if (filters.fileTypeTab !== DEFAULT_FILE_BROWSER_FILTERS.fileTypeTab) n += 1;
  if (filters.source !== DEFAULT_FILE_BROWSER_FILTERS.source) n += 1;
  if (filters.datePreset !== DEFAULT_FILE_BROWSER_FILTERS.datePreset) n += 1;
  return n;
}

function activeChips(filters: FileBrowserFilterState): {
  key: FilterDimension;
  label: string;
  icon: LucideIcon;
  clear: Partial<FileBrowserFilterState>;
}[] {
  const chips: {
    key: FilterDimension;
    label: string;
    icon: LucideIcon;
    clear: Partial<FileBrowserFilterState>;
  }[] = [];

  if (filters.fileTypeTab !== DEFAULT_FILE_BROWSER_FILTERS.fileTypeTab) {
    const opt = TYPE_OPTIONS.find((o) => o.value === filters.fileTypeTab);
    if (opt) {
      chips.push({
        key: "fileTypeTab",
        label: opt.label,
        icon: opt.icon,
        clear: { fileTypeTab: DEFAULT_FILE_BROWSER_FILTERS.fileTypeTab },
      });
    }
  }
  if (filters.source !== DEFAULT_FILE_BROWSER_FILTERS.source) {
    const opt = SOURCE_OPTIONS.find((o) => o.value === filters.source);
    if (opt) {
      chips.push({
        key: "source",
        label: opt.label,
        icon: opt.icon,
        clear: { source: DEFAULT_FILE_BROWSER_FILTERS.source },
      });
    }
  }
  if (filters.datePreset !== DEFAULT_FILE_BROWSER_FILTERS.datePreset) {
    const opt = DATE_OPTIONS.find((o) => o.value === filters.datePreset);
    if (opt) {
      chips.push({
        key: "datePreset",
        label: opt.label,
        icon: opt.icon,
        clear: { datePreset: DEFAULT_FILE_BROWSER_FILTERS.datePreset },
      });
    }
  }

  return chips;
}

/**
 * Entity-finder-style filters: search through options, grouped rows, removable chips.
 * Sort lives in {@link SortPopover}.
 */
export function FiltersPopover({
  filters,
  onChange,
  onResetFilters,
}: FiltersPopoverProps) {
  const [open, setOpen] = useState(false);
  const activeCount = countActiveFilters(filters);
  const chips = useMemo(() => activeChips(filters), [filters]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={
                activeCount > 0
                  ? `Filters: ${chips.map((c) => c.label).join(", ")}`
                  : "Filters"
              }
              className={cn(
                "inline-flex shrink-0 items-center justify-center",
                "transition-[transform,background-color,color,border-color] duration-150 ease-out",
                "hover:border-input-enhanced hover:text-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                "active:scale-[0.98]",
                activeCount > 0
                  ? cn(
                      "h-9 gap-1.5 rounded-full px-3.5 text-sm font-normal tabular-nums",
                      SURFACE_CONTROL_CLS,
                      "border-primary/40 text-foreground",
                    )
                  : SURFACE_ICON_BTN_CLS,
              )}
            >
              <ListFilter className="size-3.5 shrink-0" aria-hidden />
              {activeCount > 0 ? <span>{activeCount}</span> : null}
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {activeCount > 0
            ? `Filters: ${chips.map((c) => c.label).join(", ")}`
            : "Filters"}
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        align="start"
        className="w-[22rem] overflow-hidden rounded-xl border-border/60 p-0 shadow-xl sm:w-[24rem]"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command className="rounded-none bg-transparent">
          <CommandInput
            placeholder="Search filters…"
            className="h-11 text-sm"
          />
          <CommandList className="max-h-[min(20rem,50vh)]">
            <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
              No matching filters.
            </CommandEmpty>

            <CommandGroup heading="Type">
              {TYPE_OPTIONS.map((opt) => {
                const active = filters.fileTypeTab === opt.value;
                const Icon = opt.icon;
                return (
                  <CommandItem
                    key={`type-${opt.value}`}
                    value={`type ${opt.label}`}
                    onSelect={() => onChange({ fileTypeTab: opt.value })}
                    className="mx-1.5 gap-2 rounded-lg px-2.5 py-2 text-sm font-normal"
                  >
                    <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="flex-1 truncate">{opt.label}</span>
                    {active ? (
                      <Check className="size-3.5 shrink-0 text-foreground" aria-hidden />
                    ) : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>

            <CommandGroup heading="Source">
              {SOURCE_OPTIONS.map((opt) => {
                const active = filters.source === opt.value;
                const Icon = opt.icon;
                return (
                  <CommandItem
                    key={`source-${opt.value}`}
                    value={`source ${opt.label}`}
                    onSelect={() => onChange({ source: opt.value })}
                    className="mx-1.5 gap-2 rounded-lg px-2.5 py-2 text-sm font-normal"
                  >
                    <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="flex-1 truncate">{opt.label}</span>
                    {active ? (
                      <Check className="size-3.5 shrink-0 text-foreground" aria-hidden />
                    ) : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>

            <CommandGroup heading="Date">
              {DATE_OPTIONS.map((opt) => {
                const active = filters.datePreset === opt.value;
                const Icon = opt.icon;
                return (
                  <CommandItem
                    key={`date-${opt.value}`}
                    value={`date ${opt.label}`}
                    onSelect={() => onChange({ datePreset: opt.value })}
                    className="mx-1.5 gap-2 rounded-lg px-2.5 py-2 text-sm font-normal"
                  >
                    <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="flex-1 truncate">{opt.label}</span>
                    {active ? (
                      <Check className="size-3.5 shrink-0 text-foreground" aria-hidden />
                    ) : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>

        <div className="flex min-h-9 items-center gap-2 border-t border-border/60 px-3 py-1.5">
          <SlidersHorizontal
            size={12}
            className="shrink-0 text-muted-foreground/70"
            aria-hidden
          />
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
            {chips.length === 0 ? (
              <span className={ENTITY_FINDER_FOOTER_CLS}>All defaults</span>
            ) : (
              chips.map((chip) => {
                const Icon = chip.icon;
                return (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => onChange(chip.clear)}
                    aria-label={`Clear ${chip.label} filter`}
                    className={cn(
                      "inline-flex h-7 max-w-full items-center gap-1 rounded-full border border-border/70 bg-secondary/80 px-2 transition-colors hover:bg-accent hover:text-foreground",
                      ENTITY_FINDER_FOOTER_CHIP_CLS,
                    )}
                  >
                    <Icon size={10} className="shrink-0" aria-hidden />
                    <span className="truncate">{chip.label}</span>
                    <X size={10} className="shrink-0 text-muted-foreground" aria-hidden />
                  </button>
                );
              })
            )}
          </div>
          {chips.length > 0 ? (
            <button
              type="button"
              onClick={onResetFilters}
              className={cn(
                "shrink-0 transition-colors hover:text-foreground",
                ENTITY_FINDER_FOOTER_CLS,
              )}
            >
              Clear all
            </button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
